/**
 * Enhances Shopify Subscriptions app block labels with clearer frequency context.
 *
 * The Subscriptions widget is rendered by Shopify's hosted app script, so we treat its DOM as external.
 * We only decorate labels after render, and we keep our changes idempotent.
 */
/** @param {unknown} value */
function parseMoneyToNumber(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** @param {number} n */
function formatMoney(n) {
  // Widget labels include USD; keep it simple and consistent.
  return `$${n.toFixed(2)}`;
}

/** @param {unknown} text */
function parseIntervalWeeks(text) {
  const s = String(text ?? '').toLowerCase();
  // Matches "every week" or "every 2 weeks"
  const m = s.match(/every\s+(\d+)?\s*week/);
  if (!m) return null;
  const n = m[1] ? parseInt(m[1], 10) : 1;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** @param {unknown} container */
function enhanceContainer(container) {
  if (!(container instanceof HTMLElement)) return;

  const activeBlock =
    container.querySelector('section.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)') ||
    container.querySelector('section.shopify_subscriptions_app_block');
  if (!(activeBlock instanceof HTMLElement)) return;

  streamlineSubscriptionPricing(activeBlock);

  const planInputs = activeBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]');
  if (!planInputs.length) return;

  // Only show the hint for the currently selected subscription option to reduce visual noise.
  const checkedInput =
    activeBlock.querySelector('input[type="radio"][data-radio-type="selling_plan"]:checked') || null;

  planInputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const label = input.closest('label');
    if (!(label instanceof HTMLElement)) return;

    // Find the visible text span inside the option.
    const titleSpan = label.querySelector('.title_and_price_wrapper > span');
    if (!(titleSpan instanceof HTMLElement)) return;

    // Clear existing hint if this option is not selected.
    if (checkedInput !== input) {
      const existing = label.querySelector('.subscription-frequency-hint');
      if (existing) existing.remove();
      return;
    }

    const weeks = parseIntervalWeeks(titleSpan.textContent);
    if (!weeks) return;

    const price = parseMoneyToNumber(input.getAttribute('data-variant-price'));
    if (price == null) return;

    // Idempotent: do not add twice.
    const existing = label.querySelector('.subscription-frequency-hint');
    if (existing) {
      existing.textContent = buildHintText(price, weeks);
      return;
    }

    const hint = document.createElement('div');
    hint.className = 'subscription-frequency-hint';
    hint.style.fontSize = '0.85em';
    hint.style.opacity = '0.85';
    hint.style.marginTop = '0.25rem';
    hint.textContent = buildHintText(price, weeks);

    titleSpan.insertAdjacentElement('afterend', hint);
  });
}

/** @param {number} price @param {number} weeks */
function buildHintText(price, weeks) {
  const approxFourWeekTotal = price * (4 / weeks);
  const cadence = weeks === 1 ? 'weekly' : `every ${weeks} weeks`;
  return `Billed ${cadence}. Est. ${formatMoney(approxFourWeekTotal)} / 4 wks.`;
}

/** @param {HTMLElement} activeBlock */
function streamlineSubscriptionPricing(activeBlock) {
  // Find the "Subscribe and Save" group (the one containing selling plan radios).
  const subscriptionGroup = Array.from(activeBlock.querySelectorAll('.subscription_group')).find((group) =>
    group.querySelector('input[type="radio"][data-radio-type="selling_plan"]')
  );
  if (!(subscriptionGroup instanceof HTMLElement)) return;

  const header = subscriptionGroup.querySelector(':scope > .title_and_price_wrapper');
  if (!(header instanceof HTMLElement)) return;

  const inputs = Array.from(
    subscriptionGroup.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]')
  ).filter((el) => el instanceof HTMLInputElement);

  /** @type {number[]} */
  const prices = [];
  inputs.forEach((el) => {
    const n = parseMoneyToNumber(el.getAttribute('data-variant-price'));
    if (n != null) prices.push(n);
  });
  const unique = Array.from(new Set(prices.map((p) => p.toFixed(2))));

  // If all options share the same per-delivery price, show it once in the header.
  const headerPriceEl = header.querySelector('.subscription-group-price');
  if (unique.length === 1) {
    const priceText = `${formatMoney(Number(unique[0]))} USD`;

    let priceNode = headerPriceEl;
    if (!(priceNode instanceof HTMLElement)) {
      priceNode = document.createElement('div');
      priceNode.className = 'in_widget_price subscription-group-price';
      priceNode.setAttribute('aria-hidden', 'true');
      header.appendChild(priceNode);
    }
    priceNode.textContent = priceText;

    subscriptionGroup.querySelectorAll('li .in_widget_price').forEach((el) => {
      if (el instanceof HTMLElement) el.style.display = 'none';
    });

    // Remove repeated ", 10% off" from each option label (keep header intact).
    subscriptionGroup.querySelectorAll('li .title_and_price_wrapper > span').forEach((span) => {
      if (!(span instanceof HTMLElement)) return;
      const txt = span.textContent || '';
      span.textContent = txt.replace(/\s*,\s*10%\s*off\s*/i, '').trim();
    });
  } else {
    // Restore per-option prices if they differ.
    subscriptionGroup.querySelectorAll('li .in_widget_price').forEach((el) => {
      if (el instanceof HTMLElement) el.style.display = '';
    });
    if (headerPriceEl instanceof HTMLElement) headerPriceEl.remove();
  }
}

function tryEnhanceAll() {
  document.querySelectorAll('.shopify_subscriptions_app_container').forEach(enhanceContainer);
}

function init() {
  let scheduled = false;
  const scheduleEnhance = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      tryEnhanceAll();
    });
  };

  const observed = new WeakSet();

  /** @param {unknown} container */
  const observeContainer = (container) => {
    if (!(container instanceof HTMLElement)) return;
    if (observed.has(container)) return;
    observed.add(container);

    // Enhance immediately, then watch only this widget subtree.
    enhanceContainer(container);
    const obs = new MutationObserver(() => scheduleEnhance());
    obs.observe(container, { subtree: true, childList: true, characterData: true });
  };

  // Observe current containers.
  document.querySelectorAll('.shopify_subscriptions_app_container').forEach(observeContainer);

  // In case the app block is inserted later, watch for new containers.
  const rootObserver = new MutationObserver(() => {
    document.querySelectorAll('.shopify_subscriptions_app_container').forEach(observeContainer);
  });
  rootObserver.observe(document.body, { subtree: true, childList: true });

  // Run one initial pass.
  scheduleEnhance();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

